// Autocomplete search box that sits at the top of the {@link Sidebar}.
//
// Why it exists: PH10's sidebar mirrors PowerBuilder's full menu tree (~290 leaves across 9
// top-level groups). Even with collapse-state and Favorites, finding a specific screen by
// drilling the tree is slow. POC1 didn't have this; customers asked for it after the first
// demo. Pattern is GitHub-style — type a fragment, see matches with their breadcrumb path,
// arrow + Enter to jump.
//
// Indexing: walk NAV_TREE once on mount, flatten to `{ label, path, to, placeholder }`. Extras
// (Reports, Security, Ping) get included too. ACL-filtered at index-build so users never see
// screens their module-levels hide. Re-builds if `moduleLevels` changes (login / role swap).
//
// Matching: case-insensitive substring on label OR on the joined breadcrumb path. Live routes
// rank above placeholders so the user lands on a real screen by default when both match.
//
// Navigation: routes through {@code useGuardedNavigate} so unsaved-changes confirms still fire
// before leaving the current screen.

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { clsx } from 'clsx';
import { SearchIcon, CloseIcon } from '@/shared/components/icons';
import { useAuthStore } from '@/shared/auth/store';
import type { AuthSession } from '@/shared/auth/types';
import { useGuardedNavigate } from '@/shared/ui/useUnsavedChangesGuard';
import { bindFunctionKey, FOCUS_NAV_SEARCH_EVENT } from '@/shared/ui/appShortcuts';
import {
  NAV_TREE,
  EXTRA_NAV_ITEMS,
  resolveRoute,
  navItemState,
  isExtraVisible,
  type NavTreeItem,
} from './nav';

type IndexedItem = {
  /** Stable React key. */
  key: string;
  /** Leaf caption. */
  label: string;
  /** Breadcrumb path of ancestor labels (excluding the leaf). */
  path: string[];
  /** PH10 route. */
  to: string;
  placeholder: boolean;
  /** Joined search corpus, lowercased once. */
  haystack: string;
};

const MAX_RESULTS = 12;

function buildIndex(
  moduleLevels: Record<string, number>,
  session: AuthSession | null,
): IndexedItem[] {
  const out: IndexedItem[] = [];

  function walk(items: NavTreeItem[], path: string[]) {
    for (const it of items) {
      if (it.isSeparator) continue;
      const nextPath = it.label ? [...path, it.label] : path;
      if (it.children?.length) {
        walk(it.children, nextPath);
        continue;
      }
      if (!it.label) continue;
      // ADR-0067: F9 search lists only what the sidebar would let you open (enforce mode greys
      // or hides an ungranted window; log mode is today's level rule) — as Favorites do.
      if (navItemState(it, moduleLevels, session) !== 'enabled') continue;
      const route = resolveRoute(it);
      if (!route) continue; // unported leaves can't be navigated to — skip
      const leafPath = nextPath.slice(0, -1); // drop the leaf itself from path
      out.push({
        key: `tree:${route.to}:${it.id}`,
        label: it.label,
        path: leafPath,
        to: route.to,
        placeholder: !!route.placeholder,
        haystack: (it.label + ' ' + leafPath.join(' ')).toLowerCase(),
      });
    }
  }
  walk(NAV_TREE, []);

  for (const ex of EXTRA_NAV_ITEMS) {
    if (!isExtraVisible(ex, moduleLevels)) continue;
    // slice-3930: the TEMPORARY "My top screens" survey is flag-gated; reach it from the PH10 group.
    if (ex.topScreensOnly) continue;
    out.push({
      key: `extra:${ex.to}`,
      label: ex.label,
      path: ['PH10'],
      to: ex.to,
      placeholder: false,
      haystack: (ex.label + ' PH10').toLowerCase(),
    });
  }
  return out;
}

/** Score a match: lower is better. Word-prefix > generic substring; real > placeholder. */
function scoreItem(item: IndexedItem, q: string): number {
  const idx = item.haystack.indexOf(q);
  if (idx < 0) return Number.POSITIVE_INFINITY;
  const label = item.label.toLowerCase();
  let base: number;
  if (label.startsWith(q)) base = 0;
  else if (label.includes(q)) base = 100 + label.indexOf(q);
  else if (new RegExp(`\\b${escapeRe(q)}`).test(item.haystack)) base = 200 + idx;
  else base = 400 + idx;
  return base + (item.placeholder ? 1000 : 0);
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function NavSearch({
  moduleLevels,
  onSearchingChange,
}: {
  moduleLevels: Record<string, number>;
  /** Fires when the search becomes active / inactive so the host can hide the rest of the
   *  sidebar while results are on screen. "Active" means the query is non-empty. */
  onSearchingChange?: (searching: boolean) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();
  const guardedNavigate = useGuardedNavigate();

  const session = useAuthStore((s) => s.session);
  const index = useMemo(() => buildIndex(moduleLevels, session), [moduleLevels, session]);

  const results = useMemo<IndexedItem[]>(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return [];
    const scored: { item: IndexedItem; score: number }[] = [];
    for (const it of index) {
      const s = scoreItem(it, q);
      if (s !== Number.POSITIVE_INFINITY) scored.push({ item: it, score: s });
    }
    scored.sort((a, b) => a.score - b.score || a.item.label.localeCompare(b.item.label));
    return scored.slice(0, MAX_RESULTS).map((x) => x.item);
  }, [index, query]);

  // Reset active index whenever the result set changes.
  useEffect(() => {
    setActive(0);
  }, [results]);

  // Tell the host whether we have a non-empty query so it can hide the tree below us.
  const searching = query.trim().length > 0;
  useEffect(() => {
    onSearchingChange?.(searching);
  }, [searching, onSearchingChange]);

  // Global Ctrl/Cmd-K to focus the box from anywhere in the app shell.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // slice-3454: this box IS PB's Menu Navigator (Help > Menu Navigator, F9 — a searchable
  // tree of the whole menu, w_phfc_menu_str). Restore PB's accelerator, and answer the
  // sidebar leaf that dispatches the same event.
  useEffect(() => {
    const focusMe = () => {
      setOpen(true);
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    const unbindKey = bindFunctionKey('F9', focusMe);
    window.addEventListener(FOCUS_NAV_SEARCH_EVENT, focusMe);
    return () => {
      unbindKey();
      window.removeEventListener(FOCUS_NAV_SEARCH_EVENT, focusMe);
    };
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setActive(0);
  }, []);

  const reset = useCallback(() => {
    setQuery('');
    close();
    inputRef.current?.focus();
  }, [close]);

  const pickAt = useCallback(
    async (i: number) => {
      const it = results[i];
      if (!it) return;
      const ok = await guardedNavigate(it.to);
      if (ok) {
        setQuery('');
        close();
      }
    },
    [results, guardedNavigate, close],
  );

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setOpen(true);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (results.length > 0) {
        setOpen(true);
        setActive((i) => (i + 1) % results.length);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (results.length > 0) {
        setOpen(true);
        setActive((i) => (i - 1 + results.length) % results.length);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results.length > 0) void pickAt(active);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (query) reset();
      else inputRef.current?.blur();
    }
  };

  // Scroll active row into view when navigating with the keyboard.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const showDropdown = open && query.trim().length > 0;

  return (
    <div className="relative px-1 pb-1">
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-slate-400">
          <SearchIcon size={14} />
        </span>
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showDropdown && results[active] ? `${listboxId}-row-${active}` : undefined
          }
          placeholder="Search menus…   Ctrl+K"
          value={query}
          onChange={onChange}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Delay so a mousedown on a result still fires before the popup unmounts.
            window.setTimeout(() => setOpen(false), 120);
          }}
          onKeyDown={onKeyDown}
          className="w-full rounded border border-slate-300 bg-white py-1 pl-7 pr-7 text-xs text-slate-800 placeholder-slate-400 focus:border-phblue-600 focus:outline-none focus:ring-1 focus:ring-phblue-600"
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            type="button"
            onMouseDown={(e) => {
              // Use mousedown so it fires before the input's blur — keeps focus stable.
              e.preventDefault();
              reset();
            }}
            title="Clear search"
            aria-label="Clear search"
            className="absolute inset-y-0 right-1 flex items-center px-1 text-slate-400 hover:text-slate-700"
          >
            <CloseIcon size={14} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-label="Menu search results"
          className="mt-1 max-h-[calc(100vh-9rem)] overflow-y-auto rounded border border-slate-300 bg-white shadow-sm"
        >
          {results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">No matches.</div>
          ) : (
            results.map((it, i) => (
              <ResultRow
                key={it.key}
                id={`${listboxId}-row-${i}`}
                rowIndex={i}
                item={it}
                query={query.trim()}
                isActive={i === active}
                onHover={() => setActive(i)}
                onPick={() => void pickAt(i)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ResultRow({
  id,
  rowIndex,
  item,
  query,
  isActive,
  onHover,
  onPick,
}: {
  id: string;
  rowIndex: number;
  item: IndexedItem;
  query: string;
  isActive: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  return (
    <div
      id={id}
      data-row={rowIndex}
      role="option"
      aria-selected={isActive}
      onMouseEnter={onHover}
      onMouseDown={(e) => {
        // Use mousedown so the click registers before the input blurs and the popup unmounts.
        e.preventDefault();
        onPick();
      }}
      className={clsx(
        'cursor-pointer px-2 py-1 text-xs',
        isActive ? 'bg-phblue-100 text-phblue-800' : 'text-slate-700 hover:bg-slate-100',
      )}
    >
      <div className={clsx('truncate font-medium', item.placeholder && 'italic text-slate-500')}>
        {highlight(item.label, query)}
        {item.placeholder && (
          <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400">
            placeholder
          </span>
        )}
      </div>
      {item.path.length > 0 && (
        <div className="truncate text-[10px] text-slate-400">
          {item.path.map((p, i) => (
            <span key={i}>
              {i > 0 && <span className="mx-0.5">›</span>}
              {highlight(p, query)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Wrap matches of {@code q} in {@code <mark>}. Case-insensitive, single pass. */
function highlight(text: string, q: string): ReactNode {
  if (!q) return text;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const out: ReactNode[] = [];
  let i = 0;
  while (i < text.length) {
    const j = lower.indexOf(needle, i);
    if (j < 0) {
      out.push(text.slice(i));
      break;
    }
    if (j > i) out.push(text.slice(i, j));
    out.push(
      <mark key={j} className="rounded bg-amber-200 px-0.5 text-inherit">
        {text.slice(j, j + needle.length)}
      </mark>,
    );
    i = j + needle.length;
  }
  return out;
}
